import { useState, useEffect, useCallback, useRef } from 'react';

// Global interface for CMS bridge to control sliders
declare global {
  interface Window {
    __SOOMER_SLIDER_PAUSED__?: boolean;
    __SOOMER_SLIDER_CONTROLS__?: {
      nextSlide: () => void;
      prevSlide: () => void;
      goToSlide: (index: number) => void;
    };
  }
}

interface UseHeroSliderReturn {
  currentSlide: number;
  setCurrentSlide: (index: number) => void;
  nextSlide: () => void;
  prevSlide: () => void;
  goToSlide: (index: number) => void;
}

export function useHeroSlider(totalSlides: number, interval: number = 5000): UseHeroSliderReturn {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
    setResetKey(k => k + 1); // Reset timer
  }, [totalSlides]);

  const goToSlide = useCallback((index: number) => {
    if (index >= 0 && index < totalSlides) {
      setCurrentSlide(index);
      setResetKey(k => k + 1); // Reset timer when manually changing slides
    }
  }, [totalSlides]);

  // Expose controls for CMS bridge
  useEffect(() => {
    window.__SOOMER_SLIDER_CONTROLS__ = {
      nextSlide,
      prevSlide,
      goToSlide
    };
    return () => {
      delete window.__SOOMER_SLIDER_CONTROLS__;
    };
  }, [nextSlide, prevSlide, goToSlide]);

  // Auto-advance slides (respects CMS pause flag)
  // resetKey changes when user manually navigates, restarting the timer
  useEffect(() => {
    if (interval <= 0) return;

    // Clear existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      // Don't advance if CMS bridge has paused the slider
      if (window.__SOOMER_SLIDER_PAUSED__) return;
      nextSlide();
    }, interval);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [interval, nextSlide, resetKey]);

  return {
    currentSlide,
    setCurrentSlide,
    nextSlide,
    prevSlide,
    goToSlide
  };
}

export default useHeroSlider;
